import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SubmissionPublicationAppService from '../../../../application/platform/sep/submission-publication/submission-publication-app-service';
import { useFeedback } from '../../../../hooks/use-feedback';
import { usePagedList } from '../../../../hooks/use-paged-list';
import { ISubmissionPublicationPagedResponse } from '../../../../model/sep/submission-publications/response/submission-publication-paged-response';
import { parseNumberInput } from '../../../../utils/format';
import { ACTION_TYPES, getDefaultSubmissionPublicationParameters, useSubmissionPublicationSearchDispatch, useSubmissionPublicationSearchState } from "./submission-publication-search-context";

export const SUBMISSION_PUBLICATION_LIST_PATH = '/remessas-publicacao';

export const useSubmissionPublicationSearchController = () => {
    const navigate = useNavigate();
    const dispatch = useSubmissionPublicationSearchDispatch();
    const { parameters, searchTrigger } = useSubmissionPublicationSearchState();
    const { showError } = useFeedback();
    const { submissionPublicationId } = useParams();
    const list = usePagedList(SubmissionPublicationAppService.get, parameters, searchTrigger, 'Não foi possível carregar as remessas');

    // A rota de detalhes abre o drawer da remessa sobre a listagem
    const selectedSubmissionId = parseNumberInput(submissionPublicationId);

    useEffect(() => {
        loadLookups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function loadLookups() {
        try {
            const [users, status] = await Promise.all([SubmissionPublicationAppService.findUsersSend({}), SubmissionPublicationAppService.listStatus()]);
            dispatch({ type: ACTION_TYPES.setLookups, payload: { users, status } });
        } catch (error: any) {
            showError('Não foi possível carregar os filtros de pesquisa', error);
        }
    }

    function updateParameters(payload: object) {
        dispatch({ type: ACTION_TYPES.updateParameter, payload });
    }

    // Filtros apenas atualizam os parâmetros; a consulta ocorre em Consultar/Enter
    function handleOnChange(e: any) {
        const { name, value } = e.target;
        if (name === 'input-id') updateParameters({ id: parseNumberInput(value) });
        else if (name === 'select-type') updateParameters({ types: value });
        else if (name === 'select-status') updateParameters({ statusId: value });
        else if (name === 'select-user-id') updateParameters({ userSenderId: value === '' ? undefined : value });
        else if (name === 'select-client') updateParameters({ clientId: parseNumberInput(value) });
    }

    function handleStartDateChange(value: string | null) {
        updateParameters({ startDate: value ?? undefined });
    }

    function handleEndDateChange(value: string | null) {
        updateParameters({ endDate: value ?? undefined });
    }

    function handleSearch() {
        updateParameters({ page: 0 });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    function handleClearFilters() {
        updateParameters({ ...getDefaultSubmissionPublicationParameters(), pageSize: parameters.pageSize });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    function handlePaginationChange(page: number, pageSize: number) {
        updateParameters({ page, pageSize });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    function openDetail(row: ISubmissionPublicationPagedResponse) {
        navigate(`${SUBMISSION_PUBLICATION_LIST_PATH}/detalhes/${row.id}`);
    }

    function closeDetail() {
        navigate(SUBMISSION_PUBLICATION_LIST_PATH);
    }

    return {
        list,
        selectedSubmissionId,
        handleOnChange,
        handleStartDateChange,
        handleEndDateChange,
        handleSearch,
        handleClearFilters,
        handlePaginationChange,
        openDetail,
        closeDetail
    };
}
